import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { TaxFormServices } from '@/modules/taxForms/taxFormServices'
import { getStartAndEndDate } from '@/enums/period-select.enum'
import lang from '@/lang'

import getSummary from '@/modules/taxForms/_helper/getSummary'
import { getArgValues } from '@/modules/taxForms/_helper/doCustomCalculation'
import getTransferObject from '@/modules/taxForms/_helper/getTaxFormTransferObject'
import { TRANSFER_OBJECT_TYPE } from '@/modules/taxForms/constants'
import { FehlerRegelpruefung, Hinweis, ProcessResult, TaxFormMetaData, TaxFormMetaDataEuer } from '@/modules/taxForms/taxFormTypes'
import { isEqual, omit } from 'lodash'
import { RootState } from '@/redux/store'
import { PERIODS } from '@/modules/vatReturn/constants'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'

const PROTOCOL_REFRESH_DELAY = 3000

export type ErrorMessage = {
  key: string;
  values: string[];
}

type CalculationOptions = {
  taxFormKey: string
  year: number
  period: string
  label?: string
  isCorrection?: boolean
  customTenantId?: string
  origin?: 'dialog' | string
  isNew?: boolean
  tenDaysRule?: boolean
}

export type State = {
  currentOpenTaxFormKey: string;
  isCalculating: boolean;
  cancelCalculation: boolean;
  isFault: boolean;
  selectedTocEntry: object;
  refreshProtocolIsRunning: boolean;
  temporaryProtocolRefreshData: object;
  refreshTimeoutId: number;
  refreshProtocolData: object;
  calculationOptions: CalculationOptions;
  calculationResult: TaxFormMetaData | TaxFormMetaDataEuer;
  summary: object;
  processResult: ProcessResult;
  ignoreProcessResultHints: boolean;
  errorMessages: ErrorMessage[];
  isCalculationResultForCurrentYear: boolean;
}

const initialState: State = {
  currentOpenTaxFormKey: null,
  isCalculating: false,
  cancelCalculation: false,
  isFault: false,
  selectedTocEntry: null,
  refreshProtocolIsRunning: false,
  temporaryProtocolRefreshData: null,
  refreshTimeoutId: null,
  refreshProtocolData: null,
  calculationOptions: {
    taxFormKey: '',
    year: 0,
    period: '',
    label: '',
    isCorrection: false,
    customTenantId: '',
    origin: 'dialog',
  },
  calculationResult: null as TaxFormMetaData,
  summary: {},
  errorMessages: [],
  processResult: null,
  ignoreProcessResultHints: false,
  isCalculationResultForCurrentYear: false,
}

const general = createSlice({
  name: 'taxForms/general',
  initialState,
  reducers: {
    setCurrentOpenTaxFormKey: (state, action: PayloadAction<string>) => {
      state.currentOpenTaxFormKey = action.payload
    },
    setCalculationOptions: (state, action: PayloadAction<CalculationOptions>) => {
      state.calculationOptions = {
        ...state.calculationOptions,
        customTenantId: initialState.calculationOptions.customTenantId,
        origin: initialState.calculationOptions.origin,
        ...action.payload,
      }
    },
    reset: () => {
      return initialState
    },
    resetCalculation: (state) => {
      return { ...initialState, currentOpenTaxFormKey: state.currentOpenTaxFormKey }
    },
    setIsCalculation: (state, action: PayloadAction<boolean>) => {
      state.isCalculating = action.payload
      state.cancelCalculation = false
    },
    setCalculationResult: (state, action: PayloadAction<TaxFormMetaData>) => {
      state.calculationResult = action.payload
      state.isCalculationResultForCurrentYear = state.calculationResult?.customProps?.year === new Date().getFullYear() || state.calculationOptions.period !== PERIODS.YEAR
      state.isFault = false
      state.cancelCalculation = false
    },
    // just for testing
    setIsCalculationResultForCurrentYear: (state, action: PayloadAction<boolean>) => {
      state.isCalculationResultForCurrentYear = action.payload
    },
    cancelCalculation: (state, action: PayloadAction<boolean>) => {
      state.cancelCalculation = action.payload
      state.isFault = false
    },
    setSelectedTocEntry: (state, action: PayloadAction<object>) => {
      state.selectedTocEntry = action.payload
    },
    setSummary: (state, action: PayloadAction<object>) => {
      state.summary = action.payload
    },
    setProcessResult: (state, action: PayloadAction<ProcessResult>) => {
      state.processResult = action.payload
      if (state.processResult?.Hinweis && !Array.isArray(state.processResult?.Hinweis)) {
        state.processResult.Hinweis = [state.processResult.Hinweis as Hinweis]
      }
      if (state.processResult?.FehlerRegelpruefung && !Array.isArray(state.processResult?.FehlerRegelpruefung)) {
        state.processResult.FehlerRegelpruefung = [state.processResult.FehlerRegelpruefung as FehlerRegelpruefung]
      }
    },
    setIgnoreProcessResultHints: (state, action: PayloadAction<boolean>) => {
      state.ignoreProcessResultHints = action.payload
    },
    setErrorMessages: (state, action: PayloadAction<ErrorMessage[]>) => {
      state.errorMessages = action.payload
    },
    setRefreshProtocolIsRunning: (state, action: PayloadAction<boolean>) => {
      state.refreshProtocolIsRunning = action.payload
    },
    setTemporaryProtocolRefreshData: (state, action: PayloadAction<object>) => {
      state.temporaryProtocolRefreshData = action.payload
    },
    setRefreshTimeoutId: (state, action: PayloadAction<number>) => {
      state.refreshTimeoutId = action.payload
    },
    refreshProtocol: (state, action: PayloadAction<object>) => {
      state.refreshProtocolIsRunning = false
      state.refreshProtocolData = { timestamp: Date.now(), ...action.payload }
    },
    setFaultingState: (state, action: PayloadAction<boolean>) => {
      state.isFault = action.payload
    },
  },

})

export default general.reducer

export const { setSelectedTocEntry, setErrorMessages, cancelCalculation, refreshProtocol, reset,
  resetCalculation, setCalculationOptions, setCalculationResult, setCurrentOpenTaxFormKey,
  setFaultingState, setIsCalculation, setProcessResult, setRefreshProtocolIsRunning, setSummary,
  setTemporaryProtocolRefreshData, setRefreshTimeoutId, setIgnoreProcessResultHints,
  setIsCalculationResultForCurrentYear } = general.actions

export const updateSummary = (summaryMetaData, dataSource, transientFormValueHandler = null) => {
  return (dispatch) => {
    if (!summaryMetaData) {
      return
    }
    if (!dataSource) {
      return
    }

    const summaryMeta = Object.values(summaryMetaData)
    const tmpDataSource = summaryMeta.reduce((target, elm) => {
      if (elm.method && elm.args) {
        elm.args.forEach(a => {
          if (typeof a === 'string') {
            target[a] = getArgValues({ args: [a] }, dataSource, transientFormValueHandler)[0] ?? 0
          }
        })
      }
      return target
    }, {})

    const summary = getSummary(tmpDataSource, summaryMetaData)
    dispatch(setSummary(summary))
  }
}

export const triggerRefreshProtocol = (delayed, formValues, transientFormValueHandler = null) => {
  return (dispatch, getState) => {
    const { taxForms: { general, settings } } = getState()

    const tmpFormValues = transientFormValueHandler != null ? transientFormValueHandler.update(formValues) : formValues

    if (isEqual(general.temporaryProtocolRefreshData, tmpFormValues)) {
      return
    }

    dispatch(setRefreshProtocolIsRunning(true))
    clearTimeout(general.refreshTimeoutId)

    if (delayed) {
      const newTimeoutId = setTimeout(() => {
        const { taxForms: { general, settings } } = getState()
        const data = getTransferObject(TRANSFER_OBJECT_TYPE.REPORT_ONLY,
          general.temporaryProtocolRefreshData, general.calculationResult, general.calculationOptions, { taxSettings: settings })

        if (!isEqual(omit(general.refreshProtocolData, ['timestamp']), data)) {
          dispatch(refreshProtocol(data))
        }
        dispatch(setRefreshProtocolIsRunning(false))
      }, PROTOCOL_REFRESH_DELAY)

      dispatch(setTemporaryProtocolRefreshData(tmpFormValues))
      dispatch(setRefreshTimeoutId(Number(newTimeoutId)))
    } else {
      const data = getTransferObject(TRANSFER_OBJECT_TYPE.REPORT_ONLY,
        tmpFormValues, general.calculationResult, general.calculationOptions, { taxSettings: settings })
      dispatch(refreshProtocol(data))
      dispatch(setRefreshProtocolIsRunning(false))
    }
  }
}

export const startCalculation = (silentMode = false) => {
  return (dispatch, getState) => {
    const { taxForms: { general } } = getState()
    if (!general.currentOpenTaxFormKey) {
      toast.showToast({ type: 'error', message: lang.taxForms.noCurrentTaxFormKeyErrorMessage })
      return
    }
    if (general.currentOpenTaxFormKey !== general.calculationOptions?.taxFormKey) {
      toast.showToast({ type: 'error', message: lang.taxForms.invalidTaxFormKeyStateErrorMessage })
      return
    }

    dispatch(setIsCalculation(true))
    dispatch(setCalculationResult(null))
    dispatch(setSummary({}))
    dispatch(setProcessResult(null))
    dispatch(setIgnoreProcessResultHints(false))
    dispatch(setErrorMessages([]))

    const period = getStartAndEndDate(general.calculationOptions.period, general.calculationOptions.year)

    const params = {
      customTenantId: general.calculationOptions.customTenantId,
      origin: general.calculationOptions.origin,
      tenDaysRule: general.calculationOptions.tenDaysRule,
    }
    return TaxFormServices.getTaxFormMetaData(general.currentOpenTaxFormKey, period, params)
      .then(res => {
        dispatch(setCalculationResult(res))
        if (!silentMode) toast.showToast({ message: lang.taxForms.calculationFinishedMessage })
      })
      .catch(error => {
        dispatch(setFaultingState(true))
        toast.showToast({ error })
      })
      .finally(() => {
        dispatch(setIsCalculation(false))
      })
  }
}

export const openTaxFormDialog = (taxFormKey) => {
  return (dispatch) => {
    dispatch(setCurrentOpenTaxFormKey(taxFormKey))
    navigate(`/taxForms/${taxFormKey}`)
  }
}

export const openTaxReport = (taxKey) => {
  return (dispatch) => {
    dispatch(setCurrentOpenTaxFormKey(taxKey))
    navigate(`/taxReports/${taxKey}`)
  }
}

export const createElsterXml = ({ transferToBuhlArchiv = false }: { transferToBuhlArchiv?: boolean } = {}) => {
  return (dispatch, getState: () => RootState) => {
    const { taxForms: { general } } = getState()
    const { calculationOptions } = general

    if (!calculationOptions.taxFormKey) {
      toast.showToast({ type: 'error', message: lang.taxForms.noCurrentTaxFormKeyErrorMessage })
      return
    }

    if (general.isCalculating || !general.calculationResult) {
      toast.showToast({ type: 'error', message: lang.taxForms.noCalculationResultSummary })
      return
    }

    return TaxFormServices.createElsterXml(
      calculationOptions.taxFormKey,
      calculationOptions.period,
      general.calculationOptions.year,
      general.calculationResult.dataSource,
      transferToBuhlArchiv,
    )
      .then(() => {
        toast.showToast({
          message: transferToBuhlArchiv ? lang.taxForms.forms.base.primaryActionSuccessMessage.xmlToBuhlArchiv : lang.taxForms.forms.base.primaryActionSuccessMessage.xml,
        })
      })
      .catch(error => {
        toast.showToast({ error })
      })
  }
}
